/* Copyright (c) 2020-present CloudIO Inc */

import {
  ActionItemType,
  AnyItem,
  Page,
} from '../../registry/ComponentRegistry';
import { combinerOptions } from 'components/SmartSearch/types';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetFilterCombiner';

const ActionSetFilterCombinerManager: ActionManagerFn<
  'ActionSetFilterCombiner'
> = (platform: Platform): ActionManager<'ActionSetFilterCombiner'> => {
  return {
    ...baseActionManager(platform, 'ActionSetFilterCombiner'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        ['ActionSetQueryFilter', 'ActionSetFilterCombiner'] as ActionItemType[]
      ).includes(childType as ActionItemType),
    defaultChildren: ['ActionSetQueryFilter'],
    props: {
      ...baseActionProps,
      combiner: {
        label: 'Filter Combiner',
        type: 'select',
        options: combinerOptions,
      },
    },
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return [
        'ActionQuery',
        'ActionQueryChart',
        'ActionQuerySelect',
        'ActionSetFilterCombiner',
        'TriggerBeforeQuery',
      ].includes(parent.type);
    },
    name: 'Set Filter Combiner',
    getDisplayText: (item) => item.props.combiner,
    icon: { iconName: 'brackets' },
    description: `**Overview**<br/>
The Set Filter Combiner action is a logic structuring component designed to organize and group data query filters. It functions as the "parentheses" in a database query, allowing developers to construct complex logical hierarchies by grouping individual filters together under a specific operator (AND / OR).<br/><br/>
This component is essential for scenarios where simple linear filtering is insufficient. While a standard list of filters typically implies an implicit "AND" between all of them, the Filter Combiner allows for mixed logic—such as "Match (A OR B) AND C"—by acting as a container for other ActionSetQueryFilter items or even nested ActionSetFilterCombiner groups.

**Key Features**<br/>
* **Logical Grouping:** Defines how its child filters interact with each other, typically switching between "Match ALL" (AND) and "Match ANY" (OR) logic.
* **Recursive Nesting:** Explicitly allows ActionSetFilterCombiner as a child (isChildAllowed), enabling the creation of deep, multi-level logic trees (e.g., (A OR B) AND (X OR Y)).
* **Strict Context Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions within valid data querying contexts (ActionQuery, ActionQueryChart, ActionQuerySelect) or within another combiner.
* **Complex Queries:** Transforms flat filter lists into sophisticated boolean expressions required for advanced search interfaces or detailed reports.

**Properties**<br/>
* **Filter Combiner (combiner):** A selection property.
  * *Function:* Determines the logical operator applied to the children of this group.
  * *Common Options:* Typically AND (Intersection) or OR (Union).

**Use Cases**<br/>
* **Multi-Status Search:** Finding records where the Status is "Pending" OR "In Progress" (grouping these two under an OR combiner), while arguably restricting the whole set to the current User (an outer AND condition).
* **Segmented Filtering:** A dashboard chart that shows sales data for "Region = North" OR "Region = East", excluding other areas.
* **Advanced Criteria:** Constructing a query like: (Age > 18 AND Age < 65) OR (Status = 'VIP').`,
  };
};

export default ActionSetFilterCombinerManager;
